import React from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormat } from 'utils/dateFormat';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Button } from '../../_new/Button';
import plusIcon from '../../../assets/icons/new/plus.svg';

interface Props {
	signValidationError?: 'empty' | 'change' | '';
	menteeSign?: Date | null;
	mentorSign?: Date | null;
	panelType: string;
	signContract: () => void;
	info: string;
	disabled?: boolean;
	disabledTooltip?: string;
}

const SignBadge = ({ signed, date }: { signed: boolean; date?: string | false | null }) => {
	const { t } = useTranslation();
	if (signed && date) {
		return (
			<div className="flex flex-row align-items-center gap-2">
				<div className="flex flex-column align-items-center gap-1">
					<span style={{ color: '#1EA97C', fontWeight: 600 }}>{t('userPanel.contract.signed')}</span>
					<span style={{ color: '#1EA97C', fontSize: '0.85rem' }}>{date}</span>
				</div>
				<img src={plusIcon} alt="signed" style={{ width: 36, height: 36 }} />
			</div>
		);
	}
	return (
		<span style={{ color: '#e879a0', fontStyle: 'italic', fontWeight: 400, marginTop: '0.5rem' }}>
			{t('userPanel.contract.noSignature')}
		</span>
	);
};

const SignField = ({
	signValidationError,
	menteeSign,
	mentorSign,
	panelType,
	signContract,
	info,
	disabled = false,
	disabledTooltip,
}: Props) => {
	const { t } = useTranslation();
	const formattedMentorSign = mentorSign && dateFormat(mentorSign, 'date');
	const formattedMenteeSign = menteeSign && dateFormat(menteeSign, 'date');
	const titles =
		panelType === 'mentor'
			? ['Mentee:', `${t('userPanel.contract.mentor')}:`]
			: [`${t('userPanel.contract.mentor')}:`, 'Mentee:'];
	const signs =
		panelType === 'mentor'
			? [formattedMenteeSign, formattedMentorSign]
			: [formattedMentorSign, formattedMenteeSign];

	return (
		<div className="flex flex-column gap-2">
			<div className="flex justify-content-around w-full">
				{/* Pair column */}
				<div className="flex flex-column gap-2 text-center align-items-center">
					<div>{titles[0]}</div>
					<SignBadge signed={Boolean(signs[0])} date={signs[0]} />
				</div>

				{/* User column */}
				<div className="flex flex-column align-items-center gap-2 text-center">
					<div>{titles[1]}</div>
					{signs[1] ? (
						<SignBadge signed={Boolean(signs[1])} date={signs[1]} />
					) : (
						<>
							<SignBadge signed={false} />
							<span id={disabled ? 'sign-button-disabled' : undefined}>
								<Button
									label={t('userPanel.contract.sign')}
									onClick={signContract}
									aria-describedby="username2-help"
									disabled={disabled}
								/>
								{signValidationError && (
									<small id="username2-help" className="p-error block max-w-9rem text-center">
										{signValidationError ? `${t('userPanel.contract.emptyFields')}` : undefined}
									</small>
								)}
							</span>
							{disabled && disabledTooltip && (
								<Tooltip target="#sign-button-disabled" position="top">
									{disabledTooltip}
								</Tooltip>
							)}
						</>
					)}
				</div>
			</div>
			{!signs[1] && <p>{info}</p>}
		</div>
	);
};
export default SignField;
